import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { DayPart, ReservationService } from '../lib/reservation.service';
import * as ReservationListActions from '../store/actions/reservation-list.actions';
import * as ReservationListSelectors from '../store/selectors/reservation-list.selectors';
import { Reservation, ReservationPurpose, Shift, ShiftType } from './../model/reservation';
import { OnlineReservationCategory } from '@model/login-response.dto';
import * as AppSelectors from '../store/selectors/app.selectors';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { UtilsService } from '../lib/utils.service';

@Component({
  selector: 'app-blocking',
  templateUrl: './blocking.component.html',
  styleUrls: ['./blocking.component.scss']
})
export class BlockingComponent implements OnInit, OnDestroy {

  constructor(
    private store: Store<{ reservations: Reservation[], shifts: Shift[] }>,
    private reservationService: ReservationService,
    private translate: TranslateService,
    private utilsService: UtilsService) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  intervals: { value: string, display: string }[] = [];

  purposes: { value: string, display: string }[] = [];

  reservationsAndProcessedShifts$: Observable<{
    reservations: Reservation[],
    processedShifts: Shift[]
  }> = this.store.pipe(select(ReservationListSelectors.selectReservationsAndProcessedShifts));
  reservations: Reservation[] = [];
  processedShifts: Shift[] = [];
  dayParts: DayPart[] = [];

  interval$: Observable<string> = this.store.pipe(select(ReservationListSelectors.selectBlockingInterval));
  purpose$: Observable<string> = this.store.pipe(select(ReservationListSelectors.selectBlockingPurpose));

  expandedShift: Shift | null = null;
  
onlineCategories$: Observable<OnlineReservationCategory[]> = this.store.pipe(
  select(AppSelectors.selectOnlineCategories)
);
  categories: { value: string, display: string }[] = [];
  category$: Observable<string> = this.store.pipe(select(ReservationListSelectors.selectBlockingCategory));
  attendanceRate$ = this.store.pipe(select(TablesPlanSelectors.selectAttendanceRate));
  showEmptyIntervals = false;

  private attendanceRate: number = 0;

  private getExpectedCoversForInterval(intervalMinutes: number): number {
    return (this.attendanceRate / 15) * intervalMinutes;
  }
  get showAttendanceRateFeatures(): boolean {
    return this.utilsService.canWrite('activateTimePickerRate') && this.attendanceRate > 0;
  }

  ngOnInit(): void {
    this.loadDropdowns();

    if (this.utilsService.canWrite('activateTimePickerRate')) {
      this.store.dispatch(TablesPlanActions.loadSettings());

      this.attendanceRate$
        .pipe(takeUntil(this.destroy$))
        .subscribe(rate => {
          this.attendanceRate = rate ?? 0;
          this.refreshDayParts();
        });
    } else {
      this.attendanceRate = 0;
      this.refreshDayParts();
    }

  }

  changeCategory(category: string) {
    this.store.dispatch({
      type: ReservationListActions.setBlockingCategory.type,
      blockingCategory: category
    });

    this.refreshDayParts();
  }

  private async loadDropdowns() {
    const justToLoadTranslations = await firstValueFrom(this.translate.get('report.status.completed'));

    this.intervals = [
      { value: '15', display: '15 ' + this.translate.instant('blocking.labels.minutes') },
      { value: '30', display: '30 ' + this.translate.instant('blocking.labels.minutes') },
      { value: '45', display: '45 ' + this.translate.instant('blocking.labels.minutes') },
      { value: '60', display: '1 ' + this.translate.instant('blocking.labels.hour') }
    ];

    this.purposes = [
      { value: 'All', display: this.translate.instant('blocking.labels.all') },
      { value: 'Food', display: this.translate.instant('blocking.labels.food') },
      { value: 'Drink', display: this.translate.instant('blocking.labels.drink') }
    ];

    // load reservations and shifts
    this.store.dispatch({ type: ReservationListActions.loadReservations.type });

    this.reservationsAndProcessedShifts$.pipe(takeUntil(this.destroy$)).subscribe(({ reservations, processedShifts }) => {
      this.reservations = reservations;
      this.processedShifts = JSON.parse(JSON.stringify(processedShifts));

      if (processedShifts == null || processedShifts.length <= 0) {
        return;
      }

      if (this.expandedShift == null) {
        this.expandedShift = this.processedShifts[0];
      }

      this.refreshDayParts();
    });

    this.onlineCategories$
      .pipe(takeUntil(this.destroy$))
      .subscribe((onlineCategories) => {
        const visibleSorted = [...(onlineCategories ?? [])]
          .filter(category => !category.hidden)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        this.categories = [
          { value: 'All', display: this.translate.instant('blocking.labels.all') },
          ...visibleSorted
            .filter(category => !!category.id)
            .map(category => ({
              value: category.id!,
              display:
                category.nameLocalized?.trim() ||
                category.iconCaption?.trim() ||
                category.name?.trim() ||
                category.id!
            }))
        ];
      });
  }

async refreshDayParts() {
  if (this.processedShifts == null || this.processedShifts.length <= 0) {
    return;
  }

  const interval = await firstValueFrom(this.interval$);
  const purpose = await firstValueFrom(this.purpose$);
  const category = await firstValueFrom(this.category$);

  const intervalMinutes = parseInt(interval, 10);
  const expectedCoversPerPart = this.getExpectedCoversForInterval(intervalMinutes);

  const filteredReservations = (this.reservations ?? []).filter(reservation => {
    if (category === 'All') {
      return true;
    }

    return reservation?.reservationDetails?.onlineCategory?.id === category;
  });

  this.dayParts = this.reservationService.createDayParts(intervalMinutes);

  for (const reservation of filteredReservations) {
    this.reservationService.assignReservationToDayPart(
      reservation,
      this.dayParts,
      purpose as ReservationPurpose
    );
  }

  for (const shift of this.processedShifts) {
    shift.parts = [];

    const isFullDay = shift.isFullDay === true;
    const shiftReservations = isFullDay ? [...filteredReservations] : [];

    for (const part of this.dayParts) {
      const partBelongsToShift = this.reservationService.hourPartBelongsToShift(
        shift,
        part.hours,
        part.minsFrom,
        part.minsTo
      );

      if (isFullDay === false && partBelongsToShift === false) {
        continue;
      }

      const reservationsForPart = part.reservations ?? [];

      if (isFullDay === false) {
        for (const reservation of reservationsForPart) {
          shiftReservations.push(reservation);
        }
      }

      const partDetails = this.reservationService.calculateBlocking(
        reservationsForPart,
        purpose as ReservationPurpose
      );

      part.totalCovers = partDetails.totalCovers;
      part.totalRes = partDetails.totalRes;
      (part as any).expectedCovers = expectedCoversPerPart;

      shift.parts.push(part);
    }

    const totalBlocking = this.reservationService.calculateBlocking(
      shiftReservations,
      purpose as ReservationPurpose
    );

    shift.totalCovers = totalBlocking.totalCovers;
    shift.totalRes = totalBlocking.totalRes;
  }
}

  getShiftTitle(shiftType: ShiftType | undefined) {
    return this.reservationService.translateShiftTitle(shiftType);
  }

 filterShiftParts(part: DayPart, ...args: any[]) {
  const showEmptyIntervals = !!args?.[0]?.[0];

  if (showEmptyIntervals) {
    return true;
  }

  return (part.reservations?.length ?? 0) > 0;
}

  changeInterval(interval: string) {
    this.store.dispatch({ type: ReservationListActions.setBlockingInterval.type, blockingInterval: interval });
    this.refreshDayParts();
  }

  changePurpose(purpose: string) {
    this.store.dispatch({ type: ReservationListActions.setBlockingPurpose.type, blockingPurpose: purpose });
    this.refreshDayParts();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
